import { useState, useEffect } from "react";
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { startOfMonth, format, subYears, subMonths } from 'date-fns';
import TimeRangeSelector from './TimeRangeSelector';
import FilteredPostsDisplay from './FilteredPostsDisplay';
import ProductSelector from './ProductSelector';
import SwitchingReasonsHistogram from './SwitchingReasonsHistogram';
import { getAssessmentKeywords } from '../utils/assessmentKeywords';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const LeaverJoinerPlot = ({posts, stat_name, topics, product_name, assessmentData,
    // Optional shared product selection: adopted when valid for this
    // component's own filteredTopicMap; selections made here are pushed
    // back up so other sections of the page can pick them up.
    selectedProduct: sharedSelectedProduct, onProductChange}) => {
    const [data, setData] = useState([]);
    const [chartData, setChartData] = useState(null);
    const [postsReferenced, setPostsReferenced] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedDateRange, setSelectedDateRange] = useState(null);
    const [fullPeriodDateRange, setFullPeriodDateRange] = useState(null);
    const [minObservations, setMinObservations] = useState(20);
    const [timeRange, setTimeRange] = useState('3y');
    // Lazy-initialize from the shared selection (if it's one of this
    // company's topics) so local state is never empty on first render —
    // see PricingPlot.js for why an empty initial value lets
    // ProductSelector's own internal auto-select effect win a race against
    // the shared-selection logic below.
    const [selectedProduct, setSelectedProduct] = useState(() =>
        (sharedSelectedProduct && Array.isArray(topics) && topics.some(t => t?.id === sharedSelectedProduct))
            ? sharedSelectedProduct : null
    );
    const [filteredTopicMap, setFilteredTopicMap] = useState({});

    // Select a default product if none is selected yet. Prefers the
    // page-wide shared topic selection (if valid here); otherwise falls
    // back to the highest-priority topic with leaver/joiner data. This
    // replaces relying on ProductSelector's own internal auto-select,
    // which — being in a child component — would otherwise race with (and
    // sometimes win against) the shared-selection adoption below.
    useEffect(() => {
        if (selectedProduct) return;
        if (sharedSelectedProduct && filteredTopicMap[sharedSelectedProduct]) {
            setSelectedProduct(sharedSelectedProduct);
            return;
        }
        const sortedTopics = Object.values(filteredTopicMap).sort((a, b) => {
            const priorityA = a.priority === undefined ? Infinity : a.priority;
            const priorityB = b.priority === undefined ? Infinity : b.priority;
            return priorityA - priorityB;
        });
        if (sortedTopics[0]) setSelectedProduct(sortedTopics[0].id);
    }, [sharedSelectedProduct, filteredTopicMap, selectedProduct]);

    const selectProduct = (productId) => {
        setSelectedProduct(productId);
        if (onProductChange) onProductChange(productId);
    };


    const [buckets, setBuckets] = useState([
        { name: 'Strong Joiners', min: 6, max: Infinity, color: 'rgba(0, 255, 0, 0.7)' },
        { name: 'Possible Joiners', min: 1, max: 6, color: 'rgba(144, 238, 144, 0.7)' },
        { name: 'Strong Leavers', min: -Infinity, max: -4, color: 'rgba(255, 0, 0, 0.7)' },
        { name: 'Possible Leavers', min: -4, max: -1, color: 'rgba(255, 99, 71, 0.7)' },
    ]);

    // Filter topics to only include those with leaver-joiner assessment data
    useEffect(() => {
        // Ensure it's empty if inputs are missing/invalid
        if (!topics || !Array.isArray(topics)) {
            setFilteredTopicMap({});
            console.log('LeaverJoinerPlot: Missing or invalid topics prop.');
            return;
        }
        
        // If no assessment data yet, show all topics
        if (!assessmentData || !Array.isArray(assessmentData) || assessmentData.length === 0) {
            console.log('LeaverJoinerPlot: No assessment data yet, showing all topics');
            const topicMap = topics.reduce((acc, topic) => {
                if (topic && topic.id) {
                    acc[topic.id] = {
                        id: topic.id,
                        name: topic.name || topic.topic, 
                        priority: topic.priority
                    };
                }
                return acc;
            }, {});
            setFilteredTopicMap(topicMap);
            return;
        }
        
        // Group assessmentData by topic_id
        const assessmentsByTopic = {};
        assessmentData.forEach(item => {
            // Ensure item and topic_id are valid
            if (item && item.topic_id) {
                if (!assessmentsByTopic[item.topic_id]) {
                    assessmentsByTopic[item.topic_id] = [];
                }
                assessmentsByTopic[item.topic_id].push(item);
            }
        });
        console.log('LeaverJoinerPlot: Assessments grouped by topic ID:', assessmentsByTopic);
        
        // Filter topics to only include those with assessment data
        const validTopics = topics.filter(topic => 
            // Ensure topic and topic.id are valid
            topic && topic.id &&
            assessmentsByTopic[topic.id] && assessmentsByTopic[topic.id].length > 0
        );
        console.log('LeaverJoinerPlot: Topics with associated assessment data:', validTopics);
        
        // Convert to topicMap format for ProductSelector
        const topicMap = validTopics.reduce((acc, topic) => {
            // Use topic.topic as fallback for name, like in ProductSelector
            acc[topic.id] = {
                id: topic.id,
                name: topic.name || topic.topic, 
                priority: topic.priority
            };
            return acc;
        }, {});
        
        console.log('LeaverJoinerPlot: Setting Filtered Topic Map:', topicMap);
        setFilteredTopicMap(topicMap);
        
        // REMOVED: Auto-selection logic - ProductSelector handles it
    }, [topics, assessmentData]);

    const determineInitialTimeRange = (postsData) => {
        if (!postsData || postsData.length === 0) return '3y';

        const now = new Date();
        const earliestPost = Math.min(...postsData.map(post => post.time));
        const earliestDate = new Date(earliestPost * 1000);
        const yearsDiff = (now - earliestDate) / (1000 * 60 * 60 * 24 * 365);

        if (yearsDiff <= 1) return '1y';
        if (yearsDiff <= 3) return '3y';
        if (yearsDiff <= 5) return '5y';
        if (yearsDiff <= 10) return '10y';
        return 'all';
    };

    const filterPostsByProduct = (posts) => {
        if (!selectedProduct || !topics) return posts;
        
        const selectedTopic = topics.find(t => t.id === selectedProduct);
        if (!selectedTopic) return posts;
        
        return posts.filter(post => {
            // If the post has a 'topics' array that includes the selected topic ID
            if (post.topics && Array.isArray(post.topics)) {
                return post.topics.some(t => t === selectedTopic.id);
            }
            return false;
        });
    };

    // Process data from either assessmentData or posts
    useEffect(() => {
        // If assessmentData is provided, use it directly
        if (assessmentData && Array.isArray(assessmentData)) {
            console.log('Using provided assessment data for leaver-joiner:', assessmentData.length);

            // Filter assessmentData based on selectedProduct BEFORE setting state
            let dataToUse = assessmentData;
            if (selectedProduct) {
                console.log(`Filtering assessmentData for product: ${selectedProduct}`);
                dataToUse = assessmentData.filter(item => item.topic_id === selectedProduct);
                console.log(`Filtered assessmentData length: ${dataToUse.length}`);
            } else {
                 console.log('No product selected, using all assessmentData');
            }
            
            setData(dataToUse);
            
            // Set initial time range based on data
            if (!timeRange) {
                setTimeRange(determineInitialTimeRange(dataToUse));
            }
        }
        // Otherwise, fall back to filtering posts directly
        else if (Array.isArray(posts) && posts.length > 0) {
            console.log('Falling back to filtering posts for leaver-joiner data');
            
            // This path already filters by selectedProduct via filterPostsByProduct
            const filteredPosts = filterPostsByProduct(posts || []);
            const extractedData = filteredPosts
                .map(post => {
                    if (!post?.post_assessments) return null;
                    const assessment = post.post_assessments.find(assessment => assessment?.type === stat_name);
                    if (assessment) {
                        return {
                            ...assessment,
                            time: post?.time,
                            id: post?.id
                        };
                    }
                    return null;
                })
                .filter(item => item !== null)
                .filter(item => item?.rating !== null);
            
            setData(extractedData);
            
            // Set initial time range based on data
            if (!timeRange) {
                setTimeRange(determineInitialTimeRange(extractedData));
            }
        }
    }, [assessmentData, posts, selectedProduct, stat_name]);

    useEffect(() => {
        if (data.length > 0) {
            console.log('Processing chart data. Initial data length:', data.length);
            const sortedData = data.sort((a, b) => a.time - b.time);
            
            const now = new Date();
            // Use months instead of years for more precise control (1y = 12 months)
            const years = timeRange === 'all' ? null : parseInt(timeRange);
            const months = years ? years * 12 : null;
            const displayStartDate = months ? subMonths(now, months) : null;

            // Snap the data window to the start of the first display month
            const dataStartDate = displayStartDate ? new Date(displayStartDate.getFullYear(), displayStartDate.getMonth(), 1) : null;
            
            
            const filteredData = sortedData.filter(d => {
                const date = new Date(d.time * 1000);
                if (timeRange === 'all') return true;
                return date >= dataStartDate;
            });

            console.log('Filtered data length:', filteredData.length);

            // Set full period date range for the default histogram
            if (filteredData.length > 0) {
                const earliestDate = new Date(Math.min(...filteredData.map(d => d.time * 1000)));
                setFullPeriodDateRange({ start: earliestDate, end: now });
                console.log('Set fullPeriodDateRange:', { start: earliestDate, end: now });
            }

            // Calculate display range (what we show on chart)
            const displayStart = displayStartDate || startOfMonth(new Date(filteredData[0]?.time * 1000) || subYears(now, 3));
            // Only show the current month once it's at least the 2nd of the month
            const endDate = now.getDate() >= 2
                ? new Date(now.getFullYear(), now.getMonth() + 1, 0)
                : new Date(now.getFullYear(), now.getMonth(), 0);
            
            // Generate months only for display range
            const allMonths = [];
            let currentMonth = new Date(displayStart.getFullYear(), displayStart.getMonth(), 1);
            while (currentMonth <= endDate) {
                allMonths.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0));
                currentMonth.setMonth(currentMonth.getMonth() + 1);
            }
            
            const counts = allMonths.map((month, index) => {
                // Count posts in this calendar month only
                const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
                const nextMonthStart = new Date(month.getFullYear(), month.getMonth() + 1, 1);
                const monthData = filteredData.filter(d => {
                    const date = new Date(d.time * 1000);
                    return date >= monthStart && date < nextMonthStart;
                });

                const bucketCounts = buckets.map(bucket => ({
                    ...bucket,
                    count: monthData.filter(d => d.rating >= bucket.min && d.rating < bucket.max).length
                }));

                const netCount = bucketCounts.reduce((acc, bucket) => {
                    const joinLeaveWeight = bucket.name.includes('Joiner') ? 1 : -1;
                    const strengthWeight = bucket.name.includes('Strong') ? 1 : 0.5;
                    return acc + (bucket.count * joinLeaveWeight * strengthWeight);
                }, 0);

                return {
                    x: format(month, 'yyyy-MM'),
                    bucketCounts,
                    netCount,
                    originalDataPoints: monthData,
                    observationCount: monthData.length
                };
            });

            let sufficientCounts = [];
            let rollingCount = 0;
            for (let i = 0; i < counts.length; i++) {
                rollingCount += counts[i].observationCount;
                if (rollingCount >= minObservations) {
                    sufficientCounts.push(counts[i]);
                } else if (sufficientCounts.length > 0) {
                    sufficientCounts.push(counts[i]);
                }
            }

            setChartData({
                labels: sufficientCounts.map(item => item.x),
                datasets: [
                    ...buckets.map((bucket, index) => ({
                        type: 'bar',
                        label: bucket.name,
                        data: sufficientCounts.map(count => {
                            const value = count.bucketCounts[index].count;
                            return bucket.name.includes('Leaver') ? -value : value;
                        }),
                        backgroundColor: bucket.color,
                        stack: 'Stack 0',
                    })),
                    {
                        type: 'line',
                        label: 'Net',
                        data: sufficientCounts.map(count => count.netCount),
                        borderColor: 'rgb(75, 192, 192)',
                        tension: 0.1,
                    }
                ]
            });
            console.log('Chart data set successfully');
        }
    }, [data, buckets, minObservations, timeRange]);

    const handlePointClick = (event, elements) => {
        if (elements.length > 0) {
            const { datasetIndex, index } = elements[0];
            const month = chartData.labels[index];
            
            // Skip if clicking the net line chart
            if (datasetIndex >= buckets.length) return;
            
            const bucket = buckets[datasetIndex];
            // Each bar covers a single calendar month ('yyyy-MM' label)
            const [clickYear, clickMonth] = month.split('-').map(Number);
            const monthStart = new Date(clickYear, clickMonth - 1, 1);
            const monthEnd = new Date(clickYear, clickMonth, 0, 23, 59, 59, 999);
            
            let relevantPosts = [];
            
            if (assessmentData) {
                // Filter directly from assessment data
                relevantPosts = assessmentData.filter(item => {
                    if (!item) return false;

                    // Filter by selected product if one is selected
                    if (selectedProduct && item.topic_id !== selectedProduct) return false;

                    const postDate = new Date(item.time * 1000);
                    return postDate >= monthStart &&
                           postDate <= monthEnd &&
                           item.rating >= bucket.min &&
                           item.rating < bucket.max;
                }).map(item => ({
                    ...item, 
                    ratingName: stat_name,
                    ratingType: stat_name
                }));
            } else {
                // Filter from original posts data
                relevantPosts = (posts || []).filter(post => {
                    if (!post?.post_assessments) return false;
                    const assessment = post.post_assessments.find(a => a?.type === stat_name);
                    if (!assessment) return false;
                    
                    const postDate = new Date(post.time * 1000);
                    return postDate >= monthStart && 
                           postDate <= monthEnd && 
                           assessment.rating >= bucket.min && 
                           assessment.rating < bucket.max;
                }).map(post => {
                    const assessment = post.post_assessments.find(a => a?.type === stat_name);
                    return {
                        ...post,
                        rating: assessment?.rating,
                        confidence: assessment?.confidence,
                        explanation: assessment?.explanation,
                        ratingName: stat_name,
                        ratingType: stat_name
                    };
                });
            }

            setPostsReferenced(relevantPosts);
            setSelectedMonth(`${bucket.name} - ${format(monthStart, 'MMM yyyy')}`);
            setSelectedDateRange({ start: monthStart, end: monthEnd });
        }
    };

    return (
        <div className="leaver-joiner-plot">
            {/* Title showing selected product */}
            {selectedProduct && filteredTopicMap[selectedProduct] && (
                <h3 style={{ margin: '0 0 20px 0', fontSize: '20px', fontWeight: '500' }}>
                    {filteredTopicMap[selectedProduct].name} - Net Adds / Losses
                </h3>
            )}
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                    <ProductSelector
                        topicMap={filteredTopicMap}
                        selectedId={selectedProduct}
                        onSelect={selectProduct}
                        label="Product"
                    />
                </div>
                <TimeRangeSelector
                    timeRange={timeRange}
                    onTimeRangeChange={setTimeRange}
                    periods={['1y', '3y', '5y', 'all']}
                />
            </div>
            
            <div style={{ width: '100%', height: '400px' }}>
                {chartData ? (
                    <Bar
                        data={chartData}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            scales: {
                                x: { stacked: true },
                                y: { 
                                    stacked: true,
                                    title: { display: true, text: 'Count' }
                                }
                            },
                            onClick: handlePointClick,
                        }}
                    />
                ) : (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        No data available to display
                    </div>
                )}
            </div>

            {/* Default histogram for the whole period */}
            {selectedProduct && fullPeriodDateRange && !postsReferenced.length && (
                <div style={{ marginTop: '20px' }}>
                    {console.log('Rendering default histogram:', { selectedProduct, fullPeriodDateRange, postsReferencedLength: postsReferenced.length })}
                    <SwitchingReasonsHistogram
                        topicId={selectedProduct}
                        startDate={fullPeriodDateRange.start}
                        endDate={fullPeriodDateRange.end}
                        highlightedPeriod={`Full ${timeRange} period`}
                        style={{ 
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                            padding: '15px',
                            backgroundColor: '#fff'
                        }}
                    />
                </div>
            )}

            {/* Selected period histogram */}
            {selectedProduct && selectedDateRange && postsReferenced.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                    <SwitchingReasonsHistogram
                        topicId={selectedProduct}
                        startDate={selectedDateRange.start}
                        endDate={selectedDateRange.end}
                        highlightedPeriod={selectedMonth}
                        style={{ 
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                            padding: '15px',
                            backgroundColor: '#fff'
                        }}
                    />
                </div>
            )}

            {postsReferenced.length > 0 && (
                <FilteredPostsDisplay
                    posts={postsReferenced}
                    onClose={() => {
                        setPostsReferenced([]);
                        setSelectedMonth(null);
                        setSelectedDateRange(null);
                    }}
                    title="Referenced Posts"
                    subtitle={selectedMonth ? `Posts from ${selectedMonth}` : null}
                    ratingDisplayName="Net Add Rating"
                    ratingColorScheme={{ high: 0, low: 0 }}
                    autoScroll={true}
                    query={getAssessmentKeywords('leaver_joiner')}
                />
            )}
        </div>
    );
};

export default LeaverJoinerPlot;